// Active users presence service
import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  query,
  where,
  deleteDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';

// Constants
const ACTIVE_USERS_COLLECTION = 'active_users';
const INACTIVE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const CLEANUP_INTERVAL = 60 * 1000; // Clean up every minute

// Generate unique user ID for this session
const getUserId = () => {
  let userId = localStorage.getItem('rentvibe_user_id');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('rentvibe_user_id', userId);
  }
  return userId;
};

// Mark user as active
export const markUserActive = async () => {
  try {
    const userId = getUserId();
    const userRef = doc(db, ACTIVE_USERS_COLLECTION, userId);

    await setDoc(userRef, {
      userId,
      lastSeen: serverTimestamp(),
      userAgent: navigator.userAgent.substring(0, 100), // Truncate for privacy
      timestamp: Date.now(),
    }, { merge: true });

    console.log('User marked as active:', userId);
  } catch (error) {
    console.error('Error marking user active:', error);
  }
};

// Clean up inactive users
export const cleanupInactiveUsers = async () => {
  try {
    // This would typically be done server-side, but for demo purposes:
    // We'll just mark our own activity and let Firestore TTL handle cleanup
    await markUserActive();
  } catch (error) {
    console.error('Error in cleanup:', error);
  }
};

// Get active users count (users seen in last 5 minutes)
export const getActiveUsersCount = () => {
  return new Promise((resolve) => {
    const fiveMinutesAgo = Date.now() - INACTIVE_TIMEOUT;

    const q = query(
      collection(db, ACTIVE_USERS_COLLECTION),
      where('timestamp', '>', fiveMinutesAgo),
      orderBy('timestamp', 'desc'),
      limit(1000) // Reasonable limit
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const count = snapshot.size;
      resolve(count);
    }, (error) => {
      console.error('Error getting active users:', error);
      resolve(0);
    });

    // Clean up listener after 5 seconds
    setTimeout(() => {
      unsubscribe();
    }, 5000);
  });
};

// Listen to active users count changes
export const subscribeToActiveUsers = (callback) => {
  const fiveMinutesAgo = Date.now() - INACTIVE_TIMEOUT;

  const q = query(
    collection(db, ACTIVE_USERS_COLLECTION),
    where('timestamp', '>', fiveMinutesAgo),
    orderBy('timestamp', 'desc'),
    limit(1000)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const count = snapshot.size;
    callback(count);
  }, (error) => {
    console.error('Error in active users subscription:', error);
    callback(0);
  });

  return unsubscribe;
};

// Initialize presence system
export const initializePresence = () => {
  // Mark user as active immediately
  markUserActive();

  // Set up periodic activity updates
  const activityInterval = setInterval(() => {
    markUserActive();
  }, 60000); // Update every minute

  // Set up cleanup
  const cleanupInterval = setInterval(() => {
    cleanupInactiveUsers();
  }, CLEANUP_INTERVAL);

  // Handle page visibility changes
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      markUserActive();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Handle before unload
  const handleBeforeUnload = () => {
    // Note: This might not always fire, but it's good practice
    const userId = getUserId();
    try {
      const userRef = doc(db, ACTIVE_USERS_COLLECTION, userId);
      deleteDoc(userRef); // Remove on exit
    } catch (error) {
      console.error('Error removing user on exit:', error);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Return cleanup function
  return () => {
    clearInterval(activityInterval);
    clearInterval(cleanupInterval);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};
